import type { PublicationModel } from "../../types/models";
import { toValidDate } from "../../utils/date";
import { isFailedPub } from "./engine/status";

type PostLike = Pick<PublicationModel, "status" | "scheduledAt">;

/**
 * "Post now" on the read face (§12). Not a plan edit: the post's saved
 * date moves to the wall clock and the publisher cron (every minute)
 * sweeps it up — the same masking the create flow's publish-now does. So
 * the read face keeps zero plan controls; this is an act on one post, like
 * "View live", and the dialog says so ("usually live within a few
 * minutes"). Two scopes, told by position: the group header's menu fires
 * every post of the clip that can still fire, the row's menu fires its own.
 */

/**
 * A saved post the publisher is about to pick up: still "scheduled" but
 * dated at or before now, including the wait for the publisher after
 * "Post now". Not a target — it is already going — and the row says
 * POST_NOW_FIRING_LABEL instead of its minute.
 */
export const isFiringPub = (
  pub: { status: string; scheduledAt?: Date | null },
  now: Date,
): boolean => {
  if (pub.status !== "scheduled") return false;
  const date = toValidDate(pub.scheduledAt);
  return date != null && date.getTime() <= now.getTime();
};

/**
 * The posts "Post now" can fire: a scheduled post still ahead of the clock,
 * or a failed one (a retry re-books it, status → scheduled, error cleared —
 * the same write the edit face's Save makes). Never a live, in-flight,
 * draft or already-firing post.
 */
export const selectPostNowTargets = <T extends PostLike>(
  pubs: readonly T[],
  now: Date,
): T[] =>
  pubs.filter((pub) => {
    if (isFailedPub(pub)) return true;
    if (pub.status !== "scheduled") return false;
    const date = toValidDate(pub.scheduledAt);
    return date != null && date.getTime() > now.getTime();
  });

/** One post's answer to the same question, for the row's own menu. */
export const isPostNowTarget = (pub: PostLike, now: Date): boolean =>
  selectPostNowTargets([pub], now).length === 1;

/** A retry is the same write with a truer verb: the failed row's footer,
 *  the header item and the confirm all say "Retry" when every target
 *  failed, "Post" for a mixed set. */
export const formatPostNowVerb = (targets: readonly PostLike[]): string =>
  targets.length > 0 && targets.every(isFailedPub) ? "Retry now" : "Post now";

/** The header menu's one item names its scope: the count when the clip
 *  has several posts left to fire, the bare verb when it has one. */
export const formatPostNowMenuLabel = (targets: readonly PostLike[]): string => {
  const verb = formatPostNowVerb(targets);
  return targets.length >= 2
    ? verb.replace(" now", ` all ${targets.length} now`)
    : verb;
};

/** The confirm's subtitle: who, and the honest latency — cron wait plus a
 *  render when the clip has no fresh export is minutes, not seconds. */
export const formatPostNowConfirmDescription = (accountCount: number): string =>
  `Posting to ${accountCount} account${accountCount === 1 ? "" : "s"}. Usually live within a few minutes.`;

export const POST_NOW_FIRING_LABEL = "Posting soon";
export const POST_NOW_FIRING_HINT = "Usually live within a few minutes.";
